import { ThreeBounce } from "better-react-spinkit";
import { observer } from "mobx-react-lite";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { FaCheckCircle } from "react-icons/fa";
import { Button, Modal, ModalBody, ModalFooter } from "reactstrap";
import GCaptcha from "../../pages/Authentication/GCaptcha";
import OtpInput from "../../pages/Authentication/OtpInput";
import { DialogContext } from "../../store/context/DialogContext";
import { useTranslation } from "../../store/context/TranslationContext";
import { doGET, doPOST, doPUT } from "../../util/HttpUtil";
import { normalizeCountryCode } from "../../util/dialCode";
import CountrySelectService from "../CSC/CountrySelect/CountrySelectService";
import PhoneNumWithValidation from "./PhoneNumWithValidation";
import "./styles.css";

const btnStyle = {
  backgroundColor: '#1678AE',
  height: "40px",
  padding: "0 16px",
  fontSize: "16px",
  borderRadius: "10px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#FFF"
};

const resolveOtpVerificationEndpoint = (group) => {
  if (group === "Businesses_Lite") return "business-lite/update";
  if (group === "Business") return "business/update";
  return "individual-lite/do-otp-verification";
};

const PhoneWithOtpValidation = ({
  id,
  className,
  label = "Mobile Phone",
  value,
  onChange,
  isDisabled,
  formData,
  showErr,
  required,
  group = "Individuals_Lite",
  form_id,
  onError = () => { },
  type,
  onOtpVerify = () => { },
  onShouldPerformOtpVerificationChange = () => { },
  placeholder = "Mobile Phone",
  prevValue,
  onCommit,
  notifyParent = () => { },
  forAgent = false,
  reloadApplicationInConsole,
  individual_id,
  validateOnly = false,
}) => {
  const normalizedCountryCode = normalizeCountryCode(value?.countryCode);

  const [recaptchaToken, setRecaptchaToken] = useState("");
  const [captchaError, setCaptchaError] = useState(null);
  const [resendTimer, setResendTimer] = useState(0);
  const [sysConfingValidateMobileByOTP, setSysConfingValidateMobileByOTP] = useState(true);
  const [isCurrentCountryOtpByPassed, setIsCurrentCountryOtpByPassed] = useState(false);
  const [otpByPassedCountries, setOtpByPassedCountries] = useState([]);
  const [otp, setOtp] = useState('');
  const [otpId, setOtpId] = useState(null);
  const [otpError, setOtpError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const { showError, showMessage } = useContext(DialogContext);
  const [loading, setLoading] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const { t } = useTranslation();

  const toggleModal = () => { reset({ refreshCaptcha: false }); setModalOpen(!modalOpen) };


  const getSysConfigForMobileOtpVerification = async () => {
    try {
      const response = await doGET("/api/sys-config/validateMobileByOTP/detail");
      if (response?.status === 200) {
        setSysConfingValidateMobileByOTP(response.data?.validateMobileByOTP ?? false);
      }
    } catch (error) {

    }
  }

  const getOTPByPassedCountries = async () => {
    setLoading(true)
    try {
      const response = await doGET("/api/sys-config/otpBypassCountries/detail");
      if (response?.status === 200) {
        setOtpByPassedCountries(response.data?.otpBypassCountries ?? []);
      }
      setLoading(false)
    } catch (error) {
      setLoading(false)
    }
  }

  useEffect(() => {
    getSysConfigForMobileOtpVerification()
    getOTPByPassedCountries()
  }, [])


  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);



  const handleSendOtp = async () => {
    if (loading) return;
    if (!recaptchaToken) {
      setCaptchaError("Please complete the CAPTCHA.");
      return;
    }
    setLoading(true);
    try {
      // const fullPhoneNumber = `${value.countryCode}${value.phone}`;
      const response = await doPOST(`/api/message/send/otp`, {
        // to: fullPhoneNumber,
        cc: normalizedCountryCode,
        no: value.phone,
        ref_id: form_id,
        platform: "web",
        individual_id: form_id,
        g_captcha_response: recaptchaToken,
      });
      if (response?.data?._id) {
        setOtpId(response.data._id);
        showMessage("OTP sent successfully!");
        setModalOpen(true);
        setOtpError("");

      } else {
        showError("Failed to send OTP. Please try again.");
        reset({ refreshCaptcha: true })
      }
    } catch (error) {
      showError(error);
      reset({ refreshCaptcha: true })
    } finally {
      setLoading(false);
    }
  };

  const handleOtpVerification = async () => {
    if (loading) return;

    if (otp.length !== 4) {
      setOtpError("Please enter a 4-digit OTP.");
      return;
    }
    setLoading(true);
    try {
      const response = await doPUT(
        `/api/${resolveOtpVerificationEndpoint(group)}?otp_id=${otpId}&otpValue=${otp}&id=${form_id}`,
        { ...(formData ?? {}), mobilePhone: value?.phone, mobileCountryCode: normalizedCountryCode }
      );
      if (response.status === 200) {
        showMessage("OTP verified successfully!");
        onChange({
          ...value,
          countryCode: normalizedCountryCode,
          phoneVerifiedViaOTP: true,
          shouldPerformOtpVerification: true
        })
        onError({ errorMsg: "", hasError: false });
        onOtpVerify()
        setModalOpen(false);
      } else {
        setOtpError("Invalid OTP. Please try again.");
        reset({ refreshCaptcha: true })
      }
    } catch (error) {
      showError(error);
      reset({ refreshCaptcha: true })
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (newOtp) => {
    setOtp(newOtp);
    setOtpError(null);
  };

  const hasValue = () => {
    return normalizedCountryCode && value?.phone
  }


  const showCaptcha = () => {
    setModalOpen(true)
  }

  const reset = ({ refreshCaptcha = true }) => {
    setCaptchaError(null);
    setOtp("");
    setOtpId(null);
    setOtpError(null);
    setResendTimer(0);
  }

  const shouldPerformOtpVerification = useMemo(() => {
    return sysConfingValidateMobileByOTP && !isCurrentCountryOtpByPassed;;
  }, [sysConfingValidateMobileByOTP, isCurrentCountryOtpByPassed]);

  useEffect(() => {
    let isMounted = true;

    const syncOtpBypassState = async () => {
      if (!isMounted) return;

      if (!CountrySelectService.cyclosCountryMaster.length) {
        await CountrySelectService.fetchCyclosCountryMaster();
      }

      if (!normalizedCountryCode) {
        setIsCurrentCountryOtpByPassed(false);
        return;
      }

      const bypassCountrySet = new Set(
        (otpByPassedCountries || []).map((country) => String(country).trim().toUpperCase()).filter(Boolean)
      );
      const countriesForPincode = CountrySelectService.getIsoCodesForDialCode(normalizedCountryCode);
      const isCountryByPassed = countriesForPincode?.some((country) =>
        bypassCountrySet.has(String(country).trim().toUpperCase())
      );
      setIsCurrentCountryOtpByPassed(isCountryByPassed);
    };

    syncOtpBypassState();

    return () => {
      isMounted = false;
    };
  }, [normalizedCountryCode, otpByPassedCountries]);

  useEffect(() => {
    if (typeof onShouldPerformOtpVerificationChange === "function") {
      onShouldPerformOtpVerificationChange(shouldPerformOtpVerification)
    }
  }, [shouldPerformOtpVerification, onShouldPerformOtpVerificationChange])

  return (
    <div className={`${className} fv_phone-otp-validation`}>
      <div className="fv_phone-validation-container">
        <div className="flex-1">
          <PhoneNumWithValidation
            id={id}
            forAgent={forAgent}
            label={label}
            value={value}
            onChange={(updatedValue) => {
              onChange({ ...updatedValue, phoneVerifiedViaOTP: false });
              setIsValid(false);
            }}
            onCommit={(updatedValue) => onCommit?.(updatedValue)}
            isDisabled={loading || isDisabled || (shouldPerformOtpVerification && value?.phoneVerifiedViaOTP && value?.phone)}
            notifyParent={(nextIsValid) => {
              setIsValid(nextIsValid);
              notifyParent(nextIsValid);
            }}
            onError={(error) => {
              if (error?.hasError) setIsValid(false); else setIsValid(true);
              onError(error)
            }}
            showErr={showErr}
            required={required}
            err={hasValue() ? "" : t("Please enter a valid Mobile Phone")}
            group={group}
            form_id={form_id}
            placeholder={placeholder}
            prevValue={prevValue}
            reloadApplicationInConsole={reloadApplicationInConsole}
            individual_id={individual_id}
            validateOnly={validateOnly}
          />
          {(shouldPerformOtpVerification && !isDisabled && !forAgent && value?.phone && !value?.phoneVerifiedViaOTP && isValid) ?
            <div className="error-message mt-3"> <em className="selectfield__error">{t("Please verify mobile phone with OTP")}</em></div> : null}
        </div>
        {shouldPerformOtpVerification && !isDisabled && !forAgent ? <>
          {value?.phoneVerifiedViaOTP ? (
            <FaCheckCircle color="#28a745" size={20} title="OTP Verified" style={{ marginLeft: "10px", marginTop: '50px' }} />
          ) : (
            <Button
              style={btnStyle}
              onClick={showCaptcha}
              disabled={loading || !value.phone || !normalizedCountryCode || !isValid}
              className="fv_send-otp-button"
            >
              {loading ? <ThreeBounce size={10} color="#FFFFFF" /> : t("Send OTP")}
            </Button>
          )}</> : null}

      </div>

      {shouldPerformOtpVerification ?
        <Modal
          backdrop="static"
          isOpen={modalOpen}
          toggle={toggleModal}
          centered
          className="fv_modal-content"
        >

          <ModalBody className="fv_modal-body pt-4">

            {!otpId ? <>
              <p>{t("Solve the CAPTCHA to send OTP")}</p>
              <div className="d-flex flex-column gap-2 flex-sm-row justify-content-center">
                <div className="d-flex justify-content-center my-3">
                  <GCaptcha
                    onChange={setRecaptchaToken}
                    onExpired={() => setRecaptchaToken("")}
                  />
                </div>
                {captchaError && <p className="error-text">{captchaError}</p>}
              </div>
            </> :
              <>
                <p className="pb-3">{t("Enter the OTP sent to")} {normalizedCountryCode}-{value.phone}</p>
                <div className="fv_otp-input-container">
                  <OtpInput
                    value={otp}
                    onChange={handleOtpChange}
                    style={{ borderRadius: "14px", marginRight: "6px", border: "1px solid #C6F3FF", height: "40px", }}

                  />
                </div>
                {otpError && <div className="fv_error-message">{t(otpError)}</div>}
              </>}

          </ModalBody>
          <ModalFooter className="fv_modal-footer">
            {otpId ? <Button
              style={btnStyle}
              onClick={handleOtpVerification}
              disabled={otp.length !== 4 || loading}
              className="fv_btn-primary"
            >
              {loading ? <ThreeBounce size={10} color="#FFFFFF" /> : t("Verify OTP")}
            </Button> : <> <Button
              style={btnStyle}
              onClick={handleSendOtp}
              disabled={loading || resendTimer > 0}
            >
              {loading ? <ThreeBounce size={10} color="#FFF" /> : t("Send OTP")}
            </Button>
              {resendTimer > 0 && (
                <p className="timer-text">Resend available in {resendTimer}s</p>
              )}</>}

            <Button
              color="secondary"
              onClick={toggleModal}
              className="fv_btn-secondary"
              style={{
                backgroundColor: "#f1faff",
                color: "#007bff",
                border: "1px solid #c6f3ff",
                borderRadius: "10px",
                padding: "8px 16px",
                fontSize: "16px"
              }}
            >
              {t("Cancel")}
            </Button>
          </ModalFooter>
        </Modal>
        : null}
    </div>
  );
}

export default observer(PhoneWithOtpValidation)
